import { useEffect, useState } from 'react'
import { styled } from 'baseui'
import { Modal } from 'baseui/modal'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'
import { COMPACT_MODAL_MEDIA, COMPACT_MODAL_QUERY } from '@/components/Modal/ResponsiveModalLayout'
import DefaultButton from '@/components/DefaultButton'
import Icons from '@/scenes/Editor/components/Icons'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Z_INDEX } from '@/constants/z-index'
import ChoosePlanContent from './ChoosePlanContent'
import type { PlanTierDetail } from './planDetail'
import type { PlanType } from './MetsiKonChoosePlan'

/** Desktop shows the plan details beside the picker, so this exists only on phones. */
const IncludedLink = styled('button', {
  display: 'none',
  [COMPACT_MODAL_MEDIA]: {
    display: 'block',
    alignSelf: 'center',
    marginBottom: '12px',
    padding: '4px 8px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    ...lightTheme.typography.Small14Semibold,
    color: lightTheme.colors.blackGray,
    textDecoration: 'underline',
  },
})

const SheetControls = styled('div', {
  position: 'sticky',
  top: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '8px 12px',
  background: lightTheme.colors.white,
  zIndex: 1,
})

/**
 * Phone replacement for the "Included in…" column: a link under the plan cards
 * opens the same details in a bottom sheet, so the plan screen itself fits
 * without scrolling.
 */
export default function MobileIncludedSheet({ planActive, planDetail, planType, yearlyFromMonthlyAmount }: { planActive: number; planDetail: PlanTierDetail | null; planType: PlanType; yearlyFromMonthlyAmount: number }) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const isCompact = useMediaQuery(COMPACT_MODAL_QUERY)
  useEffect(() => {
    if (!isCompact) setIsOpen(false)
  }, [isCompact])
  const label = planType === 'premium' ? t('Included in Premium') : t('Included in Super')
  return (
    <>
      <IncludedLink type="button" onClick={() => setIsOpen(true)}>
        {label}
      </IncludedLink>
      <Modal
        isOpen={isOpen && isCompact}
        onClose={() => setIsOpen(false)}
        focusLock
        returnFocus
        overrides={{
          Root: { style: { zIndex: Z_INDEX.MODAL + 1 } },
          DialogContainer: { style: { alignItems: 'flex-end' } },
          Backdrop: { style: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } },
          Close: { component: () => null },
          Dialog: {
            props: { 'aria-label': label },
            style: {
              width: '100%',
              maxHeight: '80dvh',
              marginTop: 0,
              marginRight: 0,
              marginBottom: 0,
              marginLeft: 0,
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              paddingBottom: 'calc(56px + env(safe-area-inset-bottom))',
              backgroundColor: lightTheme.colors.white,
              textAlign: 'left',
            },
          },
        }}
      >
        <SheetControls>
          <DefaultButton
            type="minimal"
            icon="iconOnly"
            iconComponent={<Icons.Close size={24} fill={lightTheme.colors.blackGray} />}
            ariaLabel={t('Close')}
            onClick={() => setIsOpen(false)}
          />
        </SheetControls>
        <ChoosePlanContent planActive={planActive} planDetail={planDetail} planType={planType} yearlyFromMonthlyAmount={yearlyFromMonthlyAmount} />
      </Modal>
    </>
  )
}
